var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { CompositeDisposable } from "./core/lifecycle";
import { Tree } from "./core/tree";
var DEFAULT_HEIGHT = 22;
var TreeApi = /** @class */ (function () {
    function TreeApi(tree) {
        this.tree = tree;
    }
    TreeApi.prototype.setChildren = function (children, id) {
        var node;
        if (typeof id === "string") {
            node = this.tree.getNodeByIdentity(id);
        }
        this.tree.setChildren(children, node === null || node === void 0 ? void 0 : node.element);
        this.rerender();
    };
    TreeApi.prototype.rerender = function () {
        this.tree.rerender();
    };
    return TreeApi;
}());
export { TreeApi };
var Row = function (props) {
    var _a = props.data, tree = _a.tree, template = _a.template, context = _a.context;
    var list = tree.list, model = tree.model;
    var node = list.getItem(props.index);
    var isSelected = list.isSelected(props.index);
    var selectRow = function () { return list.setSelected(props.index); };
    var cn = React.useMemo(function () {
        var result = ["tree-node"];
        if (isSelected) {
            result.push("selected");
        }
        if (node.collapsible) {
            result.push("expandable");
            if (!node.collapsed) {
                result.push("expanded");
            }
        }
        return result.join(" ");
    }, [isSelected, node.collapsible]);
    return (React.createElement("div", { className: cn, style: __assign(__assign({}, props.style), { overflow: "hidden", boxSizing: "border-box" }) }, React.createElement(template, {
        tree: tree,
        index: props.index,
        isSelected: isSelected,
        selectRow: selectRow,
        node: node,
        context: context,
    })));
};
export var TreeReact = function (props) {
    var _a = __read(React.useState(0), 2), length = _a[0], setLength = _a[1];
    var listRef = React.useRef();
    var treeRef = React.useRef(new Tree({
        identity: props.identityProvider,
        collapseByDefault: typeof props.expandByDefault === "boolean"
            ? !props.expandByDefault
            : true,
    }));
    React.useEffect(function () {
        if (!props.onListHeightChanged) {
            return;
        }
        var height = 0;
        var tree = treeRef.current;
        for (var i = 0; i < tree.list.length; i++) {
            var node = tree.list.getItem(i);
            if (typeof node.height === "number") {
                height += node.height;
            }
            else {
                height += DEFAULT_HEIGHT;
            }
        }
        props.onListHeightChanged(height);
    }, [length, props.onListHeightChanged]);
    React.useEffect(function () {
        var tree = treeRef.current;
        var disposable = new CompositeDisposable(tree.list.onSelectionChanged(function () {
            var _a, _b;
            var index = tree.list.selectedIndex;
            if (index > -1 && index < tree.list.length) {
                (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.resetAfterIndex(index);
                (_b = listRef.current) === null || _b === void 0 ? void 0 : _b.scrollToItem(index, "smart");
                if (props.onSelectionChanged) {
                    var node = tree.list.getItem(index);
                    props.onSelectionChanged(node.element);
                }
            }
        }));
        return function () {
            disposable.dispose();
        };
    }, [props.onSelectionChanged]);
    React.useEffect(function () {
        var tree = treeRef.current;
        var disposable = new CompositeDisposable(tree.list.onSplice(function (index) {
            var _a;
            setLength(tree.list.length);
            (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.resetAfterIndex(index);
            treeRef.current.list.setSelected(Math.max(0, Math.min(tree.list.length - 1, tree.list.selectedIndex)));
        }));
        if (props.onReady) {
            props.onReady({ api: new TreeApi(tree) });
        }
        return function () {
            tree.dispose();
            disposable.dispose();
        };
    }, []);
    React.useEffect(function () {
        treeRef.current.model.filter = props.filter;
    }, [props.filter]);
    React.useEffect(function () {
        treeRef.current.setChildren(props.data);
        treeRef.current.rerender();
    }, [props.data]);
    var getItemSize = React.useCallback(function (index) {
        var node = treeRef.current.list.getItem(index);
        if (typeof node.height === "number") {
            return node.height;
        }
        if (typeof props.rowHeight === "number") {
            return props.rowHeight;
        }
        return DEFAULT_HEIGHT;
    }, [props.rowHeight]);
    var itemData = React.useMemo(function () {
        return {
            tree: treeRef.current,
            template: props.template,
            context: props.context,
        };
    }, [props.template]);
    var onKeyDown = React.useCallback(function (event) {
        var model = treeRef.current.model;
        var list = treeRef.current.list;
        switch (event.key) {
            case "ArrowRight": {
                var node = list.getItem(list.selectedIndex);
                model.setCollapsed(node, false);
                break;
            }
            case "ArrowLeft":
                {
                    var node = list.getItem(list.selectedIndex);
                    if (!model.setCollapsed(node, true)) {
                        var location_1 = model.getNodeLocation(node);
                        if (location_1.length > 1) {
                            location_1.splice(location_1.length - 1, 1);
                            var listIndex = model.getTreeNodeWithListIndex(location_1).listIndex;
                            list.setSelected(listIndex);
                        }
                    }
                }
                break;
            case "ArrowUp":
                list.setSelected(Math.max(0, list.selectedIndex - 1));
                break;
            case "ArrowDown":
                list.setSelected(Math.min(list.length - 1, list.selectedIndex + 1));
                break;
            case "Enter": {
                var node = list.getItem(list.selectedIndex);
                model.setCollapsed(node, !node.collapsed);
                break;
            }
            default:
                return;
        }
        event.preventDefault();
    }, []);
    var hasDimensions = React.useMemo(function () { return typeof props.height === "number" && typeof props.width === "number"; }, [props.height, props.width]);
    var ref = React.useRef(null);
    React.useEffect(function () {
        var _a;
        if (props.autoFocus) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, []);
    return (React.createElement("div", { ref: ref, className: props.className, style: {
            outline: "none",
            height: "100%",
            position: "relative",
            flex: "1 1 auto",
        }, tabIndex: 0, onKeyDown: onKeyDown }, !hasDimensions ? (React.createElement(AutoSizer, null, function (_a) {
        var width = _a.width, height = _a.height;
        return (React.createElement(VariableSizeList, { ref: listRef, height: height, width: width, itemCount: length, estimatedItemSize: props.rowHeight || DEFAULT_HEIGHT, itemSize: getItemSize, itemData: itemData }, Row));
    })) : (React.createElement(VariableSizeList, { ref: listRef, height: props.height, width: props.width, itemCount: length, estimatedItemSize: props.rowHeight || DEFAULT_HEIGHT, itemSize: getItemSize, itemData: itemData }, Row))));
};
